// One invoice-modal section that opens and closes (owner, 2026-10-08 —
// `docs/decisions.md` D-329): *"make the sections collapsible. By default,
// collapse all sections, except for Invoice Summary and Line Item Summary."*
// Flags opens by default too (owner, 2026-10-08 — D-329 amended: *"I think
// that Flags should be automatically expanded, as well."*).
//
// Every section of every invoice modal is one of these — History, Invoice
// Summary, Line Items Summary, Flags and GL Summary (AI Notes until D-330), in both the Import
// Control editor and the read-only view — so they open the same way and their
// headings sit in the same place, the left-hand corner.
//
// The heading is a button (a chevron and the title) that says whether the
// section is open (`aria-expanded`). Beside it, outside the button, sit
// `adornments` — the History and Flags pills, which show whether the section is
// open or not — and `actions`, such as Flags' "Mark All N Resolved", which is a
// button of its own and cannot sit inside another.
//
// THE WHOLE HEADER ROW TOGGLES (owner, 2026-10-08 — D-329 amended: *"clicking
// anywhere inline with the section header expands/collapses the section, so I
// do not need to precisely click on the title or icon, except of course if I am
// clicking an actual button like Flags has"*). A click anywhere on the row —
// the pills, the empty space — opens or closes the section, except a click on
// a control of its own inside `adornments` or `actions` (`headerClickToggles`,
// `app/src/lib/sections.ts`).
// The heading stays the real button, so the keyboard and a screen reader reach
// it exactly as before: Enter or Space on it is a click that reaches the row.
//
// A CLOSED SECTION STAYS MOUNTED, only hidden: a half-typed comment, the GL
// Summary's distribute switch and a line item's open picker keep their state
// when the reviewer closes and reopens the section. The open state is the
// component's own, so a modal opened on another invoice starts again from the
// defaults (the read-only view and the editor are both keyed by invoice).
import { useId, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { Button, Divider, Subtitle2, makeStyles, tokens } from '@fluentui/react-components'
import { ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons'
import { headerClickToggles } from '../lib/sections'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px' },
  head: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', cursor: 'pointer' },
  // The heading's own look, on a button: no padding, no minimum width, so the
  // title sits in the left-hand corner exactly where a bare heading did.
  toggle: {
    padding: 0,
    minWidth: 0,
    gap: '4px',
    justifyContent: 'flex-start',
    color: tokens.colorNeutralForeground1,
  },
  // At the row's far end, where Flags' "Mark All N Resolved" always sat.
  actions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' },
  body: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
})

interface CollapsibleSectionProps {
  /** The heading, as the reader sees it. Also the button's accessible name. */
  title: string
  /** Open on first render. Invoice Summary, Line Items Summary and Flags are. */
  defaultOpen?: boolean
  /** Pills beside the heading, shown open or closed. */
  adornments?: ReactNode
  /** Controls at the row's far end, outside the heading button. */
  actions?: ReactNode
  /** Draw the rule above the section. Every section but the first has one. */
  divider?: boolean
  children: ReactNode
}

export function CollapsibleSection({ title, defaultOpen = false, adornments, actions, divider = true, children }: CollapsibleSectionProps) {
  const s = useStyles()
  const [open, setOpen] = useState(defaultOpen)
  const bodyId = useId()
  const toggleRef = useRef<HTMLButtonElement>(null)
  // One handler for the whole row, the heading's button included, so a click
  // on the button is counted once.
  // A click that bubbled through a portal (a pop-up opened from a control on
  // the row) is not on the row, so it never toggles.
  const onHeadClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target instanceof Element && e.currentTarget.contains(e.target) ? e.target : null
    if (headerClickToggles(target, toggleRef.current)) setOpen((o) => !o)
  }
  return (
    <section className={s.root} aria-label={title}>
      {divider && <Divider />}
      {/* The row is not itself focusable: the heading's button is the one
          keyboard stop, and its click reaches this handler. */}
      <div className={s.head} onClick={onHeadClick}>
        <Button
          ref={toggleRef}
          appearance="transparent"
          className={s.toggle}
          icon={open ? <ChevronDownRegular /> : <ChevronRightRegular />}
          aria-expanded={open}
          aria-controls={bodyId}
        >
          <Subtitle2>{title}</Subtitle2>
        </Button>
        {adornments}
        {actions && <div className={s.actions}>{actions}</div>}
      </div>
      {/* `display: none` as well as `hidden`: the body's own `display: flex`
          would otherwise beat the browser's rule for `hidden`. */}
      <div id={bodyId} className={s.body} hidden={!open} style={open ? undefined : { display: 'none' }}>
        {children}
      </div>
    </section>
  )
}
